import { Cause, Duration, Predicate, Schedule } from 'effect';

import { CartError, CartReadAfterWriteError } from '../../capability/cart/errors';
import { isBackendApiError } from '../../data/error/backendApi/_internal/createBackendApiError';
import { ErrorTag } from '../../data/error/index';

/**
 * Statuses a proxy in front of PHP answers on its own: Varnish, the nginx
 * FastCGI bridge or the ingress. PHP can still have received the request and
 * committed it behind the proxy.
 */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([502, 503, 504]);

const isGatewayError = (u: unknown): boolean =>
  isBackendApiError(u) && GATEWAY_STATUSES.has(u.status);

/**
 * A failure that says nothing about what the backend did: the browser lost the
 * connection, the request ran out of time, or a gateway answered for PHP.
 *
 * A read that fails this way is safe to repeat. A write that fails this way has
 * an unknown outcome. The backend can have committed it.
 */
const isTransportFailure = (u: unknown): boolean =>
  Predicate.isTagged(u, ErrorTag.RequestExecutionError) ||
  Cause.isTimeoutError(u) ||
  isGatewayError(u);

/**
 * Whether a failed cart write can still have committed.
 *
 * The outcome is unknown after a transport failure, after a write that
 * committed but whose cart read failed, and after an answer the schema
 * rejects. Any other answer is definite: a 400, 401, 403, 404, 409, 422 or a
 * 500 from PHP is a refusal. The provider wraps the raw failure in a
 * {@link CartError}, so this reads the wrapped cause as well.
 */
export const isUnknownWriteOutcome = (u: unknown): boolean => {
  if (isTransportFailure(u) || u instanceof CartError.ContractMismatch) return true;
  if (!(u instanceof CartError.Unavailable)) return false;
  return u.cause instanceof CartReadAfterWriteError || isTransportFailure(u.cause);
};

/**
 * How long one cart write can take before the browser stops waiting.
 *
 * Both backends run php-fpm with `request_terminate_timeout` at 65 s. After
 * that, php-fpm kills the request and nginx answers 502. The browser waits
 * longer than that. The cart permit is therefore never released while PHP can
 * still commit the write. A second write that overlaps an unfinished add into
 * an empty cart leaves the cart unusable. Only a connection that goes silent
 * reaches this limit. The pending line stays on screen meanwhile.
 */
export const CART_WRITE_TIMEOUT = Duration.seconds(70);

/**
 * How long a write with an unknown outcome gets to finish on the server before
 * the command reads the cart to learn that outcome. A read that starts before
 * the commit returns the old cart, because the backend does not lock the order
 * for reads.
 */
export const CART_WRITE_SETTLE_DELAY = Duration.millis(1500);

/** How long one cart read can take before it counts as lost. */
export const CART_READ_TIMEOUT = Duration.seconds(10);

/**
 * How long a cart command waits for the cart atom to refresh while it holds
 * the cart permit. It covers three reads of {@link CART_READ_TIMEOUT} and the
 * retry delays between them. A read that takes longer counts as failed, so a
 * stalled read cannot keep the permit.
 */
export const CART_REFRESH_TIMEOUT = Duration.seconds(35);

/**
 * Repeats a cart read after a transport failure: 300 ms, then 600 ms, each
 * jittered. Any other failure stops the schedule and propagates unchanged.
 */
export const cartReadRetryPolicy = Schedule.exponential(Duration.millis(300)).pipe(
  Schedule.jittered,
  Schedule.upTo({ times: 2 }),
  Schedule.while((meta) => isTransportFailure(meta.input))
);
